(function () {
  const ENDPOINT = 'https://widget.trustpilot.com/trustbox-data/';
  const CACHE_PREFIX = 'et-gl3-tp:';
  const CACHE_TTL = 60 * 60 * 1000;
  const requests = {};

  function readCache(key) {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(CACHE_PREFIX + key));
      if (cached && Date.now() - cached.time < CACHE_TTL) return cached.data;
    } catch (error) {
      return null;
    }
    return null;
  }

  function writeCache(key, data) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ time: Date.now(), data }));
    } catch (error) {
      /* storage unavailable — skip caching */
    }
  }

  function fetchTrustpilot(config) {
    const params = new URLSearchParams({
      businessUnitId: config.businessUnit,
      locale: config.locale,
      reviewLanguages: config.language,
      reviewStars: config.minStars,
      reviewsPerPage: String(Math.min(100, config.count * 4)),
    });
    const url = ENDPOINT + encodeURIComponent(config.templateId) + '?' + params.toString();

    if (requests[url]) return requests[url];

    const cached = readCache(url);
    if (cached) {
      requests[url] = Promise.resolve(cached);
      return requests[url];
    }

    requests[url] = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Trustpilot request failed: ' + response.status);
        return response.json();
      })
      .then((data) => {
        writeCache(url, data);
        return data;
      });

    return requests[url];
  }

  function setStars(starsEl, rating) {
    if (!starsEl) return;
    starsEl.setAttribute('aria-label', rating + ' / 5');
    starsEl.querySelectorAll('.et-gl3-tp-star').forEach((star, index) => {
      const fill = Math.max(0, Math.min(1, rating - index)) * 100;
      star.style.setProperty('--et-gl3-tp-fill', fill + '%');
    });
  }

  function initials(name) {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].slice(0, 2);
    return words[0].charAt(0) + words[words.length - 1].charAt(0);
  }

  function renderRating(root, config, data) {
    const entity = data.businessEntity;
    if (!entity) return;

    const ratingEl = root.querySelector('[data-et-gl3-tp-rating]');
    if (ratingEl && config.ratingText) {
      const score = new Intl.NumberFormat(config.locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(entity.trustScore);
      const total = new Intl.NumberFormat(config.locale).format(entity.numberOfReviews.total);
      ratingEl.textContent = config.ratingText.replace(/\[rating\]/g, score).replace(/\[count\]/g, total);
    }

    setStars(root.querySelector('[data-et-gl3-tp-stars]'), entity.stars);

    const linkEl = root.querySelector('[data-et-gl3-tp-link]');
    if (linkEl && !linkEl.getAttribute('href') && data.links && data.links.profileUrl) {
      linkEl.setAttribute('href', data.links.profileUrl);
    }
  }

  function renderReviews(root, config, data) {
    const list = root.querySelector('[data-et-gl3-tp-list]');
    const template = root.querySelector('[data-et-gl3-tp-template]');
    if (!list || !template) return;

    const reviews = (data.reviews || []).filter((review) => review.text && review.text.trim()).slice(0, config.count);
    if (reviews.length === 0) {
      removeSkeletons(root);
      return;
    }

    const fragment = document.createDocumentFragment();
    reviews.forEach((review) => {
      const card = template.content.firstElementChild.cloneNode(true);
      const title = card.querySelector('[data-slot="title"]');
      const name = (review.consumer && review.consumer.displayName) || '';

      if (review.title && review.title.trim()) {
        title.textContent = review.title.trim();
      } else {
        title.remove();
      }
      card.querySelector('[data-slot="text"]').textContent = review.text.trim();
      card.querySelector('[data-slot="name"]').textContent = name;
      card.querySelector('[data-slot="initials"]').textContent = initials(name);
      setStars(card.querySelector('.et-gl3-tp-stars'), review.stars);
      fragment.appendChild(card);
    });

    list.replaceChildren(fragment);
  }

  function removeSkeletons(root) {
    const list = root.querySelector('[data-et-gl3-tp-list]');
    if (!list) return;
    list.querySelectorAll('.et-gl3-tp__card--skeleton').forEach((skeleton) => skeleton.remove());
    if (!list.children.length) list.remove();
  }

  function initTrustpilot(root) {
    if (root.dataset.etGl3Init) return;
    root.dataset.etGl3Init = 'true';

    const config = {
      businessUnit: root.dataset.businessUnit,
      templateId: root.dataset.templateId,
      locale: root.dataset.locale || document.documentElement.lang || 'nl-NL',
      language: root.dataset.language || 'nl',
      minStars: root.dataset.minStars || '5',
      count: parseInt(root.dataset.count, 10) || 4,
      ratingText: root.dataset.ratingText || '',
      liveRating: root.dataset.liveRating === 'true',
      liveReviews: root.dataset.liveReviews === 'true',
    };

    if (!config.businessUnit || !config.templateId || (!config.liveRating && !config.liveReviews)) {
      removeSkeletons(root);
      return;
    }

    fetchTrustpilot(config)
      .then((data) => {
        if (config.liveRating) renderRating(root, config, data);
        if (config.liveReviews) renderReviews(root, config, data);
      })
      .catch(() => removeSkeletons(root));
  }

  function initAll(scope) {
    scope.querySelectorAll('[data-et-gl3-tp]').forEach(initTrustpilot);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
